import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api, initCsrf } from '../../services/api';
import { getStoredLanguage, type SupportedLanguage } from '../../i18n/storage';
import { rememberOwnName } from '../../gameplay/playerNameStorage';
import type { PlayerGender } from '../../gameplay/coupleSetup';
import {
  clearLastUser,
  readLastUser,
  writeLastUser,
  type LastUserMarker,
} from '../lastUserMarker';

interface User {
  id: number;
  name: string;
  /** `null` (or empty, offline) for a guest: a guest has no email address. */
  email: string | null;
  role: string;
  preferences: Record<string, unknown>;
  /** True for a guest profile (`POST /guest`); turned off by `upgradeGuest`. */
  is_guest?: boolean;
  email_verified_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  resetEmailSent: boolean;
  resetEmailLoading: boolean;
  resetPasswordLoading: boolean;
  resetPasswordSuccess: boolean;
  /**
   * Set by a successful registration: the language the visitor was using
   * (`hc_language`) at that moment. The first game-settings load of the new
   * account turns it into the account language (see `loadAccountSettings`).
   */
  pendingLanguageSeed: SupportedLanguage | null;
  /**
   * True while `user` comes from the offline marker (`hc_last_user`) because
   * `GET /user` could not be reached. The next successful or rejected
   * `fetchCurrentUser` (run when the browser is back online) settles it.
   */
  offlineStale: boolean;
}

interface AuthHttpError {
  response?: {
    status?: number;
    data?: { message?: string; error?: string };
  };
}

/**
 * What an auth thunk rejects with. 429, 503, a missing response and (on
 * login) a 401/422 become codes that the pages translate
 * (`translateAuthError`); any other server text is kept, because it names the
 * field that failed.
 */
export function authRejection(
  error: unknown,
  { isLogin = false }: { isLogin?: boolean } = {}
): string {
  const response = (error as AuthHttpError | null)?.response;
  if (!response) {
    return 'network';
  }
  if (response.status === 429) {
    return 'rate_limited';
  }
  if (response.status === 503) {
    return 'unavailable';
  }
  if (isLogin && (response.status === 401 || response.status === 422)) {
    return 'invalid_credentials';
  }
  return response.data?.message || response.data?.error || 'generic';
}

/**
 * No response at all (offline, DNS, server unreachable), or a failure while the
 * browser reports it is offline. A 401 is never a network failure.
 */
export function isNetworkFailure(error: unknown): boolean {
  const response = (error as AuthHttpError | null)?.response;
  if (!response) {
    return true;
  }
  return (
    typeof navigator !== 'undefined' &&
    navigator.onLine === false &&
    response.status !== 401
  );
}

/** The signed-in user as far as the offline marker knows it. */
function userFromMarker(marker: LastUserMarker): User {
  return {
    id: marker.id,
    name: marker.name,
    email: marker.guest ? null : '',
    role: 'user',
    preferences: {},
    is_guest: marker.guest,
    // A placeholder date: only its presence is read until the refetch.
    email_verified_at: marker.verified ? new Date(0).toISOString() : null,
  };
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // start loading; App.tsx calls fetchCurrentUser to hydrate
  error: null,
  resetEmailSent: false,
  resetEmailLoading: false,
  resetPasswordLoading: false,
  resetPasswordSuccess: false,
  pendingLanguageSeed: null,
  offlineStale: false,
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (
    credentials: { email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      await initCsrf();
      const response = await api.post('/login', credentials);
      if (response.data?.user) {
        writeLastUser(response.data.user);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(authRejection(error, { isLogin: true }));
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (
    userData: {
      name: string;
      email: string;
      password: string;
      password_confirmation: string;
      gender: 'male' | 'female';
      birth_date: string;
      adult_terms_accepted: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      await initCsrf();
      const response = await api.post('/register', userData);
      if (response.data?.user) {
        writeLastUser(response.data.user);
      }
      // Read before any settings fetch overwrites `hc_language` with the
      // account default.
      return { ...response.data, localLanguage: getStoredLanguage() };
    } catch (error) {
      return rejectWithValue(authRejection(error));
    }
  }
);

/** What `startGuest` / `upgradeGuest` reject with when the server named fields. */
export interface GuestFailure {
  /** A code `translateAuthError` knows, or the server's own text. */
  message: string;
  /** First message per field (`email`, `password`, `birth_date`, ...), from a 422. */
  fields: Record<string, string>;
}

interface ValidationBody {
  response?: {
    status?: number;
    data?: { error?: string; errors?: Record<string, string[] | string> };
  };
}

/** Turns an axios failure of a guest call into a `GuestFailure`. */
export function guestFailure(error: unknown): GuestFailure {
  const response = (error as ValidationBody | null)?.response;
  const fields: Record<string, string> = {};
  if (response?.status === 422) {
    for (const [field, messages] of Object.entries(
      response.data?.errors ?? {}
    )) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (typeof first === 'string') fields[field] = first;
    }
  }
  if (Object.keys(fields).length > 0) {
    return { message: Object.values(fields)[0], fields };
  }
  const code = response?.data?.error;
  if (response?.status === 409 && typeof code === 'string') {
    return { message: code, fields };
  }
  return { message: authRejection(error), fields };
}

/**
 * `POST /guest`: a profile with only a name and a gender. The server signs the
 * guest in with a long-lived remember cookie and answers like register
 * (`{ user, settings }`). 409 `already_signed_in` is resolved by the page.
 */
export const startGuest = createAsyncThunk<
  { user: User },
  { name: string; gender: PlayerGender },
  { rejectValue: string }
>('auth/startGuest', async ({ name, gender }, { rejectWithValue }) => {
  try {
    await initCsrf();
    const response = await api.post('/guest', {
      name,
      gender,
      language: getStoredLanguage(),
    });
    const user = { ...response.data.user, is_guest: true } as User;
    writeLastUser(user);
    rememberOwnName(name);
    return { user };
  } catch (error) {
    if ((error as AuthHttpError | null)?.response?.status === 409) {
      return rejectWithValue('already_signed_in');
    }
    return rejectWithValue(authRejection(error));
  }
});

/**
 * `POST /guest/upgrade`: adds an email, a password and a birth date to the
 * guest profile in place (same user id, couple, games and answers).
 */
export const upgradeGuest = createAsyncThunk<
  { user: User },
  {
    email: string;
    password: string;
    password_confirmation: string;
    birth_date: string;
    adult_terms_accepted: boolean;
  },
  { rejectValue: GuestFailure }
>('auth/upgradeGuest', async (details, { rejectWithValue }) => {
  try {
    await initCsrf();
    const response = await api.post('/guest/upgrade', details);
    const user = {
      ...response.data.user,
      is_guest: false,
      // The server sends the verification mail; until it is followed the
      // address is unverified (and the verification banner shows).
      email_verified_at: response.data.user?.email_verified_at ?? null,
    } as User;
    writeLastUser(user);
    // The server regenerated the session: pick up the new XSRF cookie now.
    try {
      await initCsrf();
    } catch {
      // The next request refreshes it through the 419 path anyway.
    }
    return { user };
  } catch (error) {
    return rejectWithValue(guestFailure(error));
  }
});

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/user');
      const user = {
        id: response.data.id,
        name: response.data.name,
        email: response.data.email ?? null,
        role: response.data.role,
        preferences: response.data.preferences || {},
        is_guest: response.data.is_guest === true,
        email_verified_at: response.data.email_verified_at,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
      } as User;
      writeLastUser(user);
      return { user, offlineStale: false };
    } catch (error) {
      if (isNetworkFailure(error)) {
        // Cannot tell: keep the last known user rather than the sign-in form.
        const remembered = readLastUser();
        if (remembered) {
          return { user: userFromMarker(remembered), offlineStale: true };
        }
        return rejectWithValue('network');
      }
      clearLastUser();
      return rejectWithValue('Session expired or not authenticated');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/logout');
      clearLastUser();
      return true;
    } catch {
      // Server-side logout failed; still clear client state
      clearLastUser();
      return rejectWithValue('Logout failed');
    }
  }
);

export const sendResetLink = createAsyncThunk(
  'auth/sendResetLink',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/password/email', { email });
      return response.data.message;
    } catch (error) {
      return rejectWithValue(authRejection(error));
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    data: {
      token: string;
      email: string;
      password: string;
      password_confirmation: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/password/reset', data);
      return response.data.message;
    } catch (error) {
      return rejectWithValue(authRejection(error));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    clearAuth: state => {
      state.user = null;
      state.isAuthenticated = false;
      state.offlineStale = false;
      state.error = null;
    },
    updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    clearPendingLanguageSeed: state => {
      state.pendingLanguageSeed = null;
    },
    clearResetState: state => {
      state.resetEmailSent = false;
      state.resetPasswordSuccess = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.offlineStale = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.user = null;
        state.isAuthenticated = false;
      });

    builder
      .addCase(registerUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingLanguageSeed = action.payload.localLanguage ?? null;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.offlineStale = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.user = null;
        state.isAuthenticated = false;
      });

    builder
      .addCase(startGuest.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startGuest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.offlineStale = false;
        state.error = null;
      })
      .addCase(startGuest.rejected, (state, action) => {
        state.isLoading = false;
        // 409: a session already exists; the page refetches the user.
        state.error =
          action.payload && action.payload !== 'already_signed_in'
            ? action.payload
            : null;
        state.user = null;
        state.isAuthenticated = false;
      });

    builder
      .addCase(upgradeGuest.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.offlineStale = false;
      })
      .addCase(upgradeGuest.rejected, (state, action) => {
        // 409 `not_a_guest`: the profile was already saved elsewhere.
        if (action.payload?.message === 'not_a_guest' && state.user) {
          state.user = { ...state.user, is_guest: false };
        }
      });

    builder
      .addCase(fetchCurrentUser.pending, state => {
        // A refetch behind the offline shell must not blank the screen.
        if (!state.offlineStale) {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.offlineStale = action.payload.offlineStale;
        state.error = null;
      })
      .addCase(fetchCurrentUser.rejected, state => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.offlineStale = false;
      });

    builder
      .addCase(logoutUser.pending, state => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.offlineStale = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, state => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.offlineStale = false;
      });

    builder
      .addCase(sendResetLink.pending, state => {
        state.resetEmailLoading = true;
        state.error = null;
        state.resetEmailSent = false;
      })
      .addCase(sendResetLink.fulfilled, state => {
        state.resetEmailLoading = false;
        state.resetEmailSent = true;
        state.error = null;
      })
      .addCase(sendResetLink.rejected, (state, action) => {
        state.resetEmailLoading = false;
        state.error = action.payload as string;
        state.resetEmailSent = false;
      });

    builder
      .addCase(resetPassword.pending, state => {
        state.resetPasswordLoading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, state => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = true;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.error = action.payload as string;
        state.resetPasswordSuccess = false;
      });
  },
});

export const {
  clearError,
  setUser,
  clearAuth,
  updateUserProfile,
  clearResetState,
  clearPendingLanguageSeed,
} = authSlice.actions;

// Preserve legacy exports that other files may import (no-ops now)
export const setToken = setUser as unknown as typeof setUser;
export const initializeAuth = clearError as unknown as typeof clearError;
export const restoreAuthState = clearError as unknown as typeof clearError;
export const ensureAuthConsistency = clearError as unknown as typeof clearError;

export default authSlice.reducer;
